// Pure aggregation helpers for the Skills → Development tab.
// No Firebase or Vue imports — everything here is unit-testable standalone.
import format from 'date-fns/format'
import addMonths from 'date-fns/add_months'
import addDays from 'date-fns/add_days'

export const MIN_FEEDBACK_EVENTS = 2
// Manager reviews (send_review_gem) carry goal/culture ratings plus written
// comments, so a single one is enough substance to ground a summary even when
// structured peer feedback is sparse. Mirrors the backend MIN_MANAGER_REVIEWS.
export const MIN_MANAGER_REVIEWS = 1
export const STALE_AFTER_MS = 30 * 24 * 60 * 60 * 1000
export const RATING_MAP: { [k: string]: number } = { Truly: 4, Exceeding: 3, Meets: 2, Needs: 1 }

// UI label keys for the period presets (translated under skills.proficiencies.*)
export const PERIOD_LABEL_KEYS: { [preset: string]: string } = {
  'Current month': 'current_month',
  'Last month': 'last_month',
  'Past 3-months': 'past_3_months',
  'Past 6-months': 'past_6_months',
  'Past year': 'past_year',
  'All-time': 'all_time',
}

const emailKeyOf = (email: string) => email.replace(/\./g, '||')

export interface PeriodRange {
  start: string
  end: string
  prevStart: string | null
  prevEnd: string | null
  comparable: boolean
}

// Mirrors the proficiencies presets: multi-month windows get an equal-length
// prior window for deltas; All-time and single months have no comparable prior.
export const periodRange = (preset: string, companyNow: Date): PeriodRange => {
  const end = format(companyNow, 'YYYY-MM-DD')
  if (preset === 'All-time') {
    return { start: '2000-01-01', end, prevStart: null, prevEnd: null, comparable: false }
  }
  if (preset === 'Current month' || preset === 'Last month') {
    const m = preset === 'Last month'
      ? format(addMonths(companyNow, -1), 'YYYY-MM')
      : format(companyNow, 'YYYY-MM')
    // '-31' upper bound covers any month length (same trick as skill-overview)
    return { start: m + '-01', end: m + '-31', prevStart: null, prevEnd: null, comparable: false }
  }
  let months = -3
  if (preset === 'Past 6-months') months = -6
  else if (preset === 'Past year') months = -12
  const start = format(addMonths(companyNow, months), 'YYYY-MM-DD')
  const prevStart = format(addMonths(companyNow, months * 2), 'YYYY-MM-DD')
  // Prior window ends the day before the current one starts — no double-count.
  const prevEnd = format(addDays(addMonths(companyNow, months), -1), 'YYYY-MM-DD')
  return { start, end, prevStart, prevEnd, comparable: true }
}

export const monthKeysBetween = (start: string, end: string): string[] => {
  const keys: string[] = []
  let cursor = new Date(start.slice(0, 7) + '-01T00:00:00')
  const endKey = end.slice(0, 7)
  while (format(cursor, 'YYYY-MM') <= endKey) {
    keys.push(format(cursor, 'YYYY-MM'))
    cursor = addMonths(cursor, 1)
  }
  return keys
}

interface MentionEntry { count: number; attributes: { [label: string]: number } }
export interface PersonFeedback {
  given: number
  received: number
  giverCount: number
  ratingSum: number
  ratingCount: number
  ratingavg: number | null
  months: { [month: string]: { sum: number; count: number } }
  excels: { [skill: string]: MentionEntry }
  improves: { [skill: string]: MentionEntry }
}

const emptyPerson = (): PersonFeedback => ({
  given: 0, received: 0, giverCount: 0, ratingSum: 0, ratingCount: 0, ratingavg: null,
  months: {}, excels: {}, improves: {},
})

const tallyMentions = (bucket: { [skill: string]: MentionEntry }, list: any[]) => {
  if (!Array.isArray(list)) return
  list.forEach((entry: any) => {
    if (!entry || !entry.name) return
    if (!Object.prototype.hasOwnProperty.call(bucket, entry.name)) bucket[entry.name] = { count: 0, attributes: {} }
    bucket[entry.name].count++
    if (Array.isArray(entry.selected)) {
      entry.selected.forEach((a: any) => {
        const label = a && a.text_en ? a.text_en : null
        if (label) {
          bucket[entry.name].attributes[label] =
            (Object.prototype.hasOwnProperty.call(bucket[entry.name].attributes, label) ? bucket[entry.name].attributes[label] : 0) + 1
        }
      })
    }
  })
}

// Per-person rollup of raw peerfeedback docs. Keyed by emailKey ('.' → '||').
export const tallyFeedback = (docs: any[]): { [emailKey: string]: PersonFeedback } => {
  const rows: { [emailKey: string]: PersonFeedback } = {}
  // distinct givers per recipient (roster "peers" signal). Sets sidestep the
  // prototype-key hazard of a plain object; hasOwnProperty guards the lookup.
  const giverSets: { [emailKey: string]: Set<string> } = {}
  const ensure = (email: string) => {
    const key = emailKeyOf(email)
    if (!rows[key]) rows[key] = emptyPerson()
    return rows[key]
  }
  docs.forEach((data: any) => {
    if (!data) return
    if (data.email) ensure(data.email).given++
    if (!data.email_to) return
    const row = ensure(data.email_to)
    row.received++
    // giver identity: raw email (may be a phone number) or sender_name fallback
    const gid = data.email || data.sender_name
    if (gid) {
      const rkey = emailKeyOf(data.email_to)
      if (!Object.prototype.hasOwnProperty.call(giverSets, rkey)) giverSets[rkey] = new Set()
      giverSets[rkey].add(String(gid))
    }
    const tier = data.suggest && data.suggest.line1_en ? data.suggest.line1_en : null
    const rating = tier && Object.prototype.hasOwnProperty.call(RATING_MAP, tier) ? RATING_MAP[tier] : 0
    if (rating) {
      row.ratingSum += rating
      row.ratingCount++
      const month = typeof data.date === 'string' ? data.date.slice(0, 7) : null
      if (month) {
        if (!Object.prototype.hasOwnProperty.call(row.months, month)) row.months[month] = { sum: 0, count: 0 }
        row.months[month].sum += rating
        row.months[month].count++
      }
    }
    tallyMentions(row.excels, data.excels)
    tallyMentions(row.improves, data.improves)
  })
  for (const key in rows) {
    const r = rows[key]
    r.ratingavg = r.ratingCount > 0 ? Math.round((r.ratingSum / r.ratingCount) * 10) / 10 : null
    r.giverCount = Object.prototype.hasOwnProperty.call(giverSets, key) ? giverSets[key].size : 0
  }
  return rows
}

// Growth Plan answers: data.users requested feedback from each named reviewer.
export const tallyRequests = (docs: any[]): { [emailKey: string]: { requested: number; asked: number } } => {
  const rows: { [emailKey: string]: { requested: number; asked: number } } = {}
  const ensure = (email: string) => {
    const key = emailKeyOf(email)
    if (!rows[key]) rows[key] = { requested: 0, asked: 0 }
    return rows[key]
  }
  docs.forEach((data: any) => {
    if (!data || !data.users) return
    const answers = Array.isArray(data.answer) ? data.answer : (data.answer ? [data.answer] : [])
    if (answers.length === 0) return
    ensure(data.users).requested += answers.length
    answers.forEach((a: any) => {
      const reviewer = a && a.email ? a.email : (typeof a === 'string' ? a : null)
      if (reviewer) ensure(reviewer).asked++
    })
  })
  return rows
}

export const topMention = (bucket: { [skill: string]: { count: number } }): { name: string; count: number } | null => {
  let best: { name: string; count: number } | null = null
  for (const name of Object.keys(bucket).sort()) {
    if (!best || bucket[name].count > best.count) best = { name, count: bucket[name].count }
  }
  return best
}

export const ratingDelta = (
  cur: PersonFeedback | undefined,
  prev: PersonFeedback | undefined,
): number | null => {
  if (!cur || !prev || !cur.ratingCount || !prev.ratingCount) return null
  if (cur.ratingavg === null || prev.ratingavg === null) return null
  return Math.round((cur.ratingavg - prev.ratingavg) * 10) / 10
}

export const ratingSeries = (
  person: PersonFeedback | undefined,
  start: string,
  end: string,
): Array<{ month: string; avg: number | null; count: number }> => {
  return monthKeysBetween(start, end).map((month) => {
    const m = person && person.months[month] ? person.months[month] : null
    return {
      month,
      avg: m && m.count > 0 ? Math.round((m.sum / m.count) * 10) / 10 : null,
      count: m ? m.count : 0,
    }
  })
}

// develop_skills createdate arrives as a Firestore Timestamp, Date, or string.
const toDateSafe = (v: any): Date | null => {
  if (!v) return null
  if (typeof v.toDate === 'function') return v.toDate()
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}

export interface JourneyStats { completed: number; active: number; learningMinutes: number; categories: { [tag: string]: number } }

// Single pass over all develop_skills docs → per-emailKey stats. The roster
// loop consumes this map; journeyStats() below is the single-person view.
export const tallyJourneys = (
  docs: any[],
  start: string | null,
  end: string | null,
): { [emailKey: string]: JourneyStats } => {
  const rows: { [emailKey: string]: JourneyStats } = {}
  docs.forEach((data: any) => {
    if (!data || !data.email) return
    if (start && end) {
      const d = toDateSafe(data.createdate)
      if (!d) return
      // NOTE: createdate renders in the browser TZ while start/end derive from
      // company-timezone "now" — worst case one day of drift at window edges.
      const day = format(d, 'YYYY-MM-DD')
      if (day < start || day > end) return
    }
    const key = data.email.replace(/\./g, '||')
    if (!Object.prototype.hasOwnProperty.call(rows, key)) {
      rows[key] = { completed: 0, active: 0, learningMinutes: 0, categories: {} }
    }
    const out = rows[key]
    if (data.finish) out.completed++
    else out.active++
    // parseFloat rejects null/''/false (NaN) — an explicit degenerate read must not count 15 min
    const read = typeof data.read === 'number' ? data.read : parseFloat(data.read)
    if (!isNaN(read)) out.learningMinutes += (read + 1) * 15
    const tag = data.category_tag || 'Uncategorized'
    out.categories[tag] = (out.categories[tag] || 0) + 1
  })
  return rows
}

const EMPTY_JOURNEYS: JourneyStats = { completed: 0, active: 0, learningMinutes: 0, categories: {} }

export const journeyStats = (
  docs: any[],
  email: string,
  start: string | null,
  end: string | null,
): JourneyStats => tallyJourneys(docs, start, end)[email.replace(/\./g, '||')] || { ...EMPTY_JOURNEYS, categories: {} }

export const recognitionStats = (
  docs: any[],
): { [emailKey: string]: { given: number; received: number; hashtags: { [tag: string]: number } } } => {
  const rows: { [emailKey: string]: { given: number; received: number; hashtags: { [tag: string]: number } } } = {}
  const ensure = (email: string) => {
    const key = emailKeyOf(email)
    if (!Object.prototype.hasOwnProperty.call(rows, key)) rows[key] = { given: 0, received: 0, hashtags: {} }
    return rows[key]
  }
  docs.forEach((data: any) => {
    if (!data) return
    if (data.sender_email) ensure(data.sender_email).given++
    if (data.send_to_email) {
      const row = ensure(data.send_to_email)
      row.received++
      if (Array.isArray(data.hashtag)) {
        data.hashtag.forEach((tag: any) => {
          if (tag) {
            row.hashtags[tag] =
              (Object.prototype.hasOwnProperty.call(row.hashtags, tag) ? row.hashtags[tag] : 0) + 1
          }
        })
      }
    }
  })
  return rows
}

// Latest narrative cache doc per person; prefers client createdAtMs, falls
// back to generatedAt for docs written server-side or by older clients.
export const latestPictureDocs = (docs: any[]): { [emailKey: string]: any } => {
  const out: { [emailKey: string]: any } = {}
  const stamp = (d: any) => (d && d.createdAtMs) || (d && d.generatedAt ? Date.parse(d.generatedAt) || 0 : 0)
  docs.forEach((data: any) => {
    if (!data || !data.email) return
    const key = data.email.replace(/\./g, '||')
    if (!Object.prototype.hasOwnProperty.call(out, key) || stamp(data) > stamp(out[key])) {
      out[key] = data
    }
  })
  return out
}

// Count fingerprint, not a content hash — edits that keep counts equal are only caught by the 30-day expiry.
export const computeInputHash = (counts: { feedbackEvents: number; journeys: number; recognitionEvents: number }): string =>
  `fb:${counts.feedbackEvents}|jy:${counts.journeys}|rec:${counts.recognitionEvents}`

// The roster knows only fb/jy/rec; person-page docs carry extra bo/rf segments
// (written via computeInputHashV2). A doc is "current" from the roster's view if
// every segment the roster computed is present verbatim in the doc's longer v2
// hash. Segment keys (fb:/jy:/rec:) are unique and values are exact, so `fb:1`
// won't match `fb:14`. This is a shared-prefix check, NOT strict equality — the
// person page keeps its own precise full-v2-hash isStale check.
export function inputHashCurrentForRoster (docHash: string, rosterHash: string): boolean {
  if (!docHash || !rosterHash) return false
  const docSegs = String(docHash).split('|')
  return String(rosterHash).split('|').every(seg => docSegs.indexOf(seg) >= 0)
}

// A summary is groundable when structured peer feedback clears the bar OR the
// person has at least one manager review. Either signal alone carries enough
// substance for a narrative; below both, there's nothing to summarize.
export const hasEnoughEvidence = (feedbackEvents: number, managerReviews: number = 0): boolean =>
  feedbackEvents >= MIN_FEEDBACK_EVENTS || managerReviews >= MIN_MANAGER_REVIEWS

export type NarrativeStatus = 'needs_data' | 'not_generated' | 'stale' | 'fresh'

export const narrativeStatus = (
  doc: { generatedAt: string; inputHash: string } | null,
  currentHash: string,
  feedbackEvents: number,
  nowMs: number,
  managerReviews: number = 0,
): NarrativeStatus => {
  if (!hasEnoughEvidence(feedbackEvents, managerReviews)) return 'needs_data'
  // enough evidence but no cached doc — generatable, just not generated yet
  if (!doc) return 'not_generated'
  // stale if the roster's signals no longer match the doc's (prefix-match, since
  // the doc's v2 hash carries extra bo/rf segments the roster can't reconstruct)
  if (!inputHashCurrentForRoster(doc.inputHash, currentHash)) return 'stale'
  const generatedMs = Date.parse(doc.generatedAt)
  if (!generatedMs || nowMs - generatedMs > STALE_AFTER_MS) return 'stale'
  return 'fresh'
}

// ---- v2: ordinal rating levels (spec decision 9) -------------------------
export const RATING_LEVEL_KEYS: { [level: number]: string } = {
  4: 'skills.development.rating_truly',
  3: 'skills.development.rating_exceeding',
  2: 'skills.development.rating_meeting',
  1: 'skills.development.rating_needs'
}

export function ratingLevel (avg: any): number | null {
  if (avg === '') return null
  if (avg == null || isNaN(avg)) return null
  return Math.min(4, Math.max(1, Math.round(avg)))
}

export function starsFor (level: any): string {
  const lv = ratingLevel(level)
  if (lv == null) return ''
  return '★'.repeat(lv) + '☆'.repeat(4 - lv)
}

export function ratingLevelsOf (rows: any[]): number[] {
  return (rows || [])
    .map(r => r && r.suggest && RATING_MAP[r.suggest.line1_en])
    .filter((v: any) => typeof v === 'number')
}

export function ratingRange (rows: any[]): { lo: number, hi: number } | null {
  const lv = ratingLevelsOf(rows)
  if (!lv.length) return null
  return { lo: Math.min(...lv), hi: Math.max(...lv) }
}

// ---- v2: manager review boosts (send_review_gem) -------------------------
export interface ReviewBoost {
  date: string
  month: string
  senderName: string
  goal: number | null
  culture: number | null
  hasComments: boolean
}

function validGemLevel (v: any): number | null {
  return (typeof v === 'number' && v >= 1 && v <= 4) ? v : null
}

export function gemStars (level: any): string {
  const lv = validGemLevel(level)
  if (lv == null) return '—'
  return '★'.repeat(lv) + '☆'.repeat(4 - lv)
}

export function mapBoosts (docs: any[]): ReviewBoost[] {
  return (docs || [])
    .filter(b => !!b)
    .map(b => {
      const date = String(b.createdate || '').slice(0, 10)
      return {
        date,
        month: date.slice(0, 7),
        senderName: b.sender_firstname || String(b.sender || '').split('@')[0],
        goal: validGemLevel(b.goal),
        culture: validGemLevel(b.culture),
        // 'hightlight' (sic) is a misspelled field present on real production docs — keep the check.
        hasComments: !!(b.goal_value || b.culture_value || b.highlight || b.hightlight)
      }
    })
    .filter(b => !!b.date)
    .sort((a, b) => (a.date < b.date ? -1 : (a.date > b.date ? 1 : 0)))
}

// Roster: manager-review (send_review_gem) count per receiver within [start,end].
// Docs are queried company-wide (equality-only) and filtered client-side by
// createdate here, so no composite index is needed. Keyed by emailKey.
export function tallyBoostsByPerson (docs: any[], start: string, end: string): { [emailKey: string]: number } {
  const out: { [emailKey: string]: number } = {}
  ;(docs || []).forEach((d: any) => {
    if (!d || !d.receiver) return
    const date = String(d.createdate || '').slice(0, 10)
    if (!date || date < start || date > end) return
    const key = emailKeyOf(d.receiver)
    out[key] = (Object.prototype.hasOwnProperty.call(out, key) ? out[key] : 0) + 1
  })
  return out
}

// ---- v2: self reflections (toneanalyze -> users_questions) ----------------
export interface ReflectionLite {
  date: string
  month: string
  question: string
  answer: string
  highlight: string
}

export function mapReflections (docs: any[], range: { start: string, end: string }): ReflectionLite[] {
  return (docs || [])
    .filter(r => !!r)
    .map(r => {
      const h = Array.isArray(r.highlights) && r.highlights.length ? r.highlights[0] : ''
      return {
        date: String(r.date || '').slice(0, 10),
        month: String(r.date || '').slice(0, 7),
        question: r.quest_name_en || r.quest_name || '',
        answer: String(r.answer || ''),
        highlight: typeof h === 'string' ? h : ((h && h.phrase) || '')
      }
    })
    .filter(r => r.answer && r.date >= range.start && r.date <= range.end)
    .sort((a, b) => (a.date < b.date ? 1 : (a.date > b.date ? -1 : 0)))
}

// ---- v2: input hash over all five signals ---------------------------------
export function computeInputHashV2 (c: { fb: number, jy: number, rec: number, boosts: number, refl: number }): string {
  return `fb:${c.fb}|jy:${c.jy}|rec:${c.rec}|bo:${c.boosts}|rf:${c.refl}`
}

// ---- v2: per-person skill tally + one-column-per-skill assignment --------
export interface PersonSkillTally {
  [name: string]: {
    excels: number
    improves: number
    behaviors: { excels: { [b: string]: number }, improves: { [b: string]: number } }
  }
}

export function tallyPersonSkills (rows: any[]): PersonSkillTally {
  const t: PersonSkillTally = {}
  const bump = (name: string, side: 'excels' | 'improves', sel: any[]) => {
    if (!name) return
    if (!t[name]) t[name] = { excels: 0, improves: 0, behaviors: { excels: {}, improves: {} } }
    t[name][side]++
    ;(sel || []).forEach(s => {
      if (s && s.text_en) t[name].behaviors[side][s.text_en] = (t[name].behaviors[side][s.text_en] || 0) + 1
    })
  }
  ;(rows || []).forEach(r => {
    if (!r) return
    ;(r.excels || []).forEach((s: any) => s && bump(s.name, 'excels', s.selected))
    ;(r.improves || r.improve || []).forEach((s: any) => s && bump(s.name, 'improves', s.selected))
  })
  return t
}

export interface SkillSideEntry {
  name: string
  count: number
  prev: number
  minority: number
  minorityBehavior: string | null
  behaviors: Array<[string, number]>
}

// ---- period comparison: skill-side classification + movements ----
// A skill's "side" in a window, using the SAME dominance rule as skillSides
// (strength when excels >= improves; growth when improves strictly greater).
export function sideOf (entry: { excels: number, improves: number } | null | undefined): 'strength' | 'growth' | null {
  if (!entry) return null
  if (entry.excels > 0 && entry.excels >= entry.improves) return 'strength'
  if (entry.improves > 0 && entry.improves > entry.excels) return 'growth'
  return null
}

export interface SkillMovement {
  name: string
  prevExcels: number
  prevImproves: number
  nowExcels: number
  nowImproves: number
}
export interface SkillMovements {
  improved: SkillMovement[]       // growth -> strength
  newStrengths: SkillMovement[]   // null   -> strength
  stillDeveloping: SkillMovement[] // growth -> growth
  watch: SkillMovement[]          // strength -> growth
}

// Compare two PersonSkillTally maps and bucket each skill into ONE movement.
// strength->strength (consistent) and any ->null (no longer cited) are NOT
// movements: silence is not improvement. Each bucket is sorted by the
// dominant-side count desc (name tiebreak) and capped.
export function skillMovements (
  prevTally: PersonSkillTally,
  nowTally: PersonSkillTally,
  cap: number = 3,
): SkillMovements {
  const out: SkillMovements = { improved: [], newStrengths: [], stillDeveloping: [], watch: [] }
  const names = new Set<string>([...Object.keys(prevTally || {}), ...Object.keys(nowTally || {})])
  names.forEach((name) => {
    const p = (prevTally || {})[name]
    const n = (nowTally || {})[name]
    const prevSide = sideOf(p)
    const nowSide = sideOf(n)
    const mv: SkillMovement = {
      name,
      prevExcels: p ? p.excels : 0,
      prevImproves: p ? p.improves : 0,
      nowExcels: n ? n.excels : 0,
      nowImproves: n ? n.improves : 0,
    }
    if (prevSide === 'growth' && nowSide === 'strength') out.improved.push(mv)
    else if (prevSide === null && nowSide === 'strength') out.newStrengths.push(mv)
    else if (prevSide === 'growth' && nowSide === 'growth') out.stillDeveloping.push(mv)
    else if (prevSide === 'strength' && nowSide === 'growth') out.watch.push(mv)
  })
  const byExcels = (a: SkillMovement, b: SkillMovement) => (b.nowExcels - a.nowExcels) || a.name.localeCompare(b.name)
  const byImproves = (a: SkillMovement, b: SkillMovement) => (b.nowImproves - a.nowImproves) || a.name.localeCompare(b.name)
  out.improved = out.improved.sort(byExcels).slice(0, cap)
  out.newStrengths = out.newStrengths.sort(byExcels).slice(0, cap)
  out.stillDeveloping = out.stillDeveloping.sort(byImproves).slice(0, cap)
  out.watch = out.watch.sort(byImproves).slice(0, cap)
  return out
}

export interface LevelShift {
  prevAvg: number | null
  nowAvg: number | null
  prevLevel: number | null
  nowLevel: number | null
  dir: 'up' | 'down' | 'hold'
}

function avgGemLevel (boosts: any[], key: 'goal' | 'culture'): number | null {
  const vals = (boosts || [])
    .map((b: any) => b && b[key])
    .filter((v: any): v is number => typeof v === 'number')
  return vals.length ? vals.reduce((a: number, b: number) => a + b, 0) / vals.length : null
}

// prev/now are ReviewBoost[] (from mapBoosts): goal/culture already validated to
// 1-4 or null. Returns null for a metric with no value in EITHER window (the UI
// hides that tile). dir is 'hold' unless both windows have a value and the delta
// exceeds 0.1 of a level.
export function managerLevelShift (prevBoosts: any[], nowBoosts: any[]): { goal: LevelShift | null, culture: LevelShift | null } {
  const build = (key: 'goal' | 'culture'): LevelShift | null => {
    const prevAvg = avgGemLevel(prevBoosts, key)
    const nowAvg = avgGemLevel(nowBoosts, key)
    if (prevAvg === null && nowAvg === null) return null
    let dir: 'up' | 'down' | 'hold' = 'hold'
    if (prevAvg !== null && nowAvg !== null) {
      const d = nowAvg - prevAvg
      dir = d > 0.1 ? 'up' : (d < -0.1 ? 'down' : 'hold')
    }
    return {
      prevAvg,
      nowAvg,
      prevLevel: prevAvg === null ? null : Math.round(prevAvg),
      nowLevel: nowAvg === null ? null : Math.round(nowAvg),
      dir,
    }
  }
  return { goal: build('goal'), culture: build('culture') }
}

export interface RecognitionTag { tag: string, count: number, isNew: boolean }

function hashtagCounts (docs: any[]): { [tag: string]: number } {
  const t: { [tag: string]: number } = {}
  ;(docs || []).forEach((d: any) => {
    if (d && Array.isArray(d.hashtag)) {
      d.hashtag.forEach((tag: any) => { if (tag) t[tag] = (t[tag] || 0) + 1 })
    }
  })
  return t
}

// prev/now are sendcoin here_point docs (each with hashtag[]). Returns the top
// `cap` current tags by count; isNew = tag absent from the ENTIRE prior window's
// tag set (a genuinely new recognition theme, not merely newly top-ranked).
export function recognitionTagShift (prevDocs: any[], nowDocs: any[], cap: number = 3): { now: RecognitionTag[] } {
  const prevCounts = hashtagCounts(prevDocs)
  const nowCounts = hashtagCounts(nowDocs)
  const now = Object.keys(nowCounts)
    .map((tag) => ({ tag, count: nowCounts[tag], isNew: !Object.prototype.hasOwnProperty.call(prevCounts, tag) }))
    .sort((a, b) => (b.count - a.count) || a.tag.localeCompare(b.tag))
    .slice(0, cap)
  return { now }
}

export function skillSides (now: PersonSkillTally, prev: PersonSkillTally, cap: number = 3): { strengths: SkillSideEntry[], growth: SkillSideEntry[] } {
  const entry = (name: string, side: 'excels' | 'improves'): SkillSideEntry => {
    const v = now[name]
    const minorKey = side === 'excels' ? 'improves' : 'excels'
    const minorBehaviors = Object.entries(v.behaviors[minorKey]).sort((a, b) => b[1] - a[1])
    return {
      name,
      count: v[side],
      prev: (prev[name] || { excels: 0, improves: 0 } as any)[side] || 0,
      minority: v[minorKey],
      minorityBehavior: minorBehaviors.length ? minorBehaviors[0][0] : null,
      behaviors: Object.entries(v.behaviors[side]).sort((a, b) => b[1] - a[1]).slice(0, 2)
    }
  }
  const names = Object.keys(now)
  const strengths = names.filter(n => now[n].excels > 0 && now[n].excels >= now[n].improves)
    .sort((a, b) => now[b].excels - now[a].excels).slice(0, cap).map(n => entry(n, 'excels'))
  const growth = names.filter(n => now[n].improves > 0 && now[n].improves > now[n].excels)
    .sort((a, b) => now[b].improves - now[a].improves).slice(0, cap).map(n => entry(n, 'improves'))
  return { strengths, growth }
}

// ---- v2: giver x month grid ------------------------------------------------
export interface GiverRow {
  label: string
  months: { [month: string]: number }
  total: number
  kind: 'mgr' | 'giver' | 'others'
}

export function giverName (r: any): string {
  return r.sender_name || String(r.email || '').split('@')[0]
}

export function giverGrid (feedback: any[], boosts: ReviewBoost[], monthKeys: string[], maxNamed: number = 6): GiverRow[] {
  const rows: GiverRow[] = []
  // Clamp both signals to the visible month columns — a boost or feedback row
  // outside monthKeys must not surface phantom counts in the grid.
  const boostsInWindow = (boosts || []).filter(b => monthKeys.indexOf(b.month) >= 0)
  if (boostsInWindow.length) {
    const mgr: GiverRow = { label: boostsInWindow[boostsInWindow.length - 1].senderName, months: {}, total: 0, kind: 'mgr' }
    boostsInWindow.forEach(b => { mgr.months[b.month] = (mgr.months[b.month] || 0) + 1; mgr.total++ })
    rows.push(mgr)
  }
  const agg: { [name: string]: GiverRow } = {}
  ;(feedback || []).forEach(r => {
    if (!r) return
    const n = giverName(r)
    const m = String(r.date || '').slice(0, 7)
    if (monthKeys.indexOf(m) < 0) return
    if (!agg[n]) agg[n] = { label: n, months: {}, total: 0, kind: 'giver' }
    agg[n].months[m] = (agg[n].months[m] || 0) + 1
    agg[n].total++
  })
  const sorted = Object.keys(agg).map(k => agg[k]).sort((a, b) => b.total - a.total)
  rows.push(...sorted.slice(0, maxNamed))
  const rest = sorted.slice(maxNamed)
  if (rest.length) {
    const others: GiverRow = { label: String(rest.length), months: {}, total: 0, kind: 'others' }
    rest.forEach(g => {
      Object.keys(g.months).forEach(m => { others.months[m] = (others.months[m] || 0) + g.months[m] })
      others.total += g.total
    })
    rows.push(others)
  }
  return rows
}

// ---- v2: journeys ----------------------------------------------------------
export interface JourneyLite {
  name: string
  cat: string
  startedMonth: string
  finish: boolean
  read: number
  qty: number
  mgr: boolean
}

export function mapJourneys (docs: any[], range: { start: string, end: string }): JourneyLite[] {
  return (docs || [])
    .filter((j: any) => !!j)
    .map((j: any) => {
      const started = String(j.createdate || '').slice(0, 10)
      return {
        started,
        name: j.set_name || '',
        cat: j.category_tag || '',
        startedMonth: started.slice(0, 7),
        finish: !!j.finish,
        read: typeof j.read === 'number' ? j.read : (parseFloat(j.read) || 0),
        qty: typeof j.qty === 'number' ? j.qty : (parseFloat(j.qty) || 0),
        mgr: !!j.review_id
      }
    })
    .filter((j: any) => j.started >= range.start && j.started <= range.end)
    .sort((a: any, b: any) => (a.started < b.started ? -1 : a.started > b.started ? 1 : 0))
    .map((j: any) => ({ name: j.name, cat: j.cat, startedMonth: j.startedMonth, finish: j.finish, read: j.read, qty: j.qty, mgr: j.mgr }))
}

// ---- v2: person-page period (6M default / 12M toggle) ----------------------
// Local-time formatting (matches v1 periodRange) and date-fns month math so
// month-end overflow clamps (Aug 31 − 6M → Feb 28, not Mar 3).
export function personPeriodRange (months: number, now?: Date): { start: string, end: string, prevStart: string, prevEnd: string } {
  const end = now || new Date()
  const start = addMonths(end, -months)
  return {
    start: format(start, 'YYYY-MM-DD'),
    end: format(end, 'YYYY-MM-DD'),
    prevStart: format(addMonths(start, -months), 'YYYY-MM-DD'),
    prevEnd: format(addDays(start, -1), 'YYYY-MM-DD')
  }
}

// ---- v2: growth moments -----------------------------------------------------
export type MomentKind = 'baseline' | 'shift' | 'review' | 'journey' | 'now'
export const MOMENT_KIND_CLASS: { [k in MomentKind]: string } = {
  baseline: 'rt', shift: 'fb', review: 'pf', journey: 'jy', now: 'rt'
}
const MOMENT_KIND_ORDER: { [k in MomentKind]: number } = { baseline: 0, review: 1, journey: 2, shift: 3, now: 4 }

export interface GrowthMoment {
  month: string
  kind: MomentKind
  title: string
  body: string
  givers?: string[]
}

function monthFeedbackRows (feedback: any[], month: string): any[] {
  return (feedback || []).filter(r => r && String(r.date || '').slice(0, 7) === month)
}

function topSkillNames (rows: any[], side: 'excels' | 'improves'): string {
  const t: { [n: string]: number } = {}
  rows.forEach(r => ((side === 'excels' ? r.excels : (r.improves || r.improve)) || []).forEach((s: any) => {
    if (s && s.name) t[s.name] = (t[s.name] || 0) + 1
  }))
  return Object.entries(t).sort((a, b) => b[1] - a[1]).slice(0, 2).map(([n]) => n).join(', ')
}

export function deterministicMoments (opts: { monthKeys: string[], feedback: any[], boosts: ReviewBoost[], journeys: JourneyLite[] }): GrowthMoment[] {
  const ms: GrowthMoment[] = []
  const fbMonths = opts.monthKeys.filter(k => monthFeedbackRows(opts.feedback, k).length)
  if (fbMonths.length) {
    const rows = monthFeedbackRows(opts.feedback, fbMonths[0])
    ms.push({ month: fbMonths[0], kind: 'baseline', title: '', body: `${topSkillNames(rows, 'excels')}|${topSkillNames(rows, 'improves')}` })
  }
  opts.boosts.forEach(b => ms.push({ month: b.month, kind: 'review', title: '', body: '' }))
  opts.journeys.filter(j => j.mgr).forEach(j => ms.push({ month: j.startedMonth, kind: 'journey', title: j.name, body: j.cat }))
  if (fbMonths.length > 1) {
    const last = fbMonths[fbMonths.length - 1]
    const rows = monthFeedbackRows(opts.feedback, last)
    ms.push({ month: last, kind: 'now', title: '', body: `${topSkillNames(rows, 'excels')}|${topSkillNames(rows, 'improves')}` })
  }
  return ms
}

export function resolveGrowthMoments (aiMoments: any, opts: { monthKeys: string[], boosts: ReviewBoost[], feedback: any[], fallback: GrowthMoment[] }): GrowthMoment[] {
  let ms: GrowthMoment[] = Array.isArray(aiMoments)
    ? aiMoments.filter((m: any) => m && opts.monthKeys.indexOf(m.month) >= 0 && Object.prototype.hasOwnProperty.call(MOMENT_KIND_CLASS, m.kind) && m.title)
      .map((m: any) => ({ month: m.month, kind: m.kind, title: String(m.title), body: String(m.body || '') }))
    : []
  if (!ms.length) ms = opts.fallback.slice()
  ms = ms.slice(0, 6)
  const reviewMonths: { [m: string]: boolean } = {}
  ms.forEach(m => { if (m.kind === 'review') reviewMonths[m.month] = true })
  opts.boosts.forEach(b => {
    if (opts.monthKeys.indexOf(b.month) < 0) return
    if (reviewMonths[b.month]) return
    reviewMonths[b.month] = true
    ms.push({ month: b.month, kind: 'review', title: '', body: '' })
  })
  ms = ms.map(m => {
    const out: GrowthMoment = { month: m.month, kind: m.kind, title: m.title, body: m.body }
    if (m.kind === 'review') {
      const b = opts.boosts.filter(x => x.month === m.month)[0]
      if (b) out.title = `${(m.title || '').replace(/\.$/, '')}${m.title ? ' · ' : ''}Goals ${gemStars(b.goal)} · Culture ${gemStars(b.culture)}`.trim()
    }
    if (m.kind !== 'review' && m.kind !== 'journey') {
      const rows = monthFeedbackRows(opts.feedback, m.month)
      if (rows.length) {
        const seen: { [n: string]: boolean } = {}
        out.givers = rows.map(giverName).filter((n: string) => (seen[n] ? false : (seen[n] = true)))
      }
    }
    return out
  })
  ms.sort((a, b) => (a.month < b.month ? -1 : a.month > b.month ? 1 : MOMENT_KIND_ORDER[a.kind] - MOMENT_KIND_ORDER[b.kind]))
  return ms
}

export function collapseJourneys (journeys: JourneyLite[], moments: GrowthMoment[]): { done: number, inProgress: number, topCategories: Array<[string, number]> } | null {
  const shown: { [m: string]: boolean } = {}
  moments.forEach(m => { if (m.kind === 'journey') shown[m.month] = true })
  const rest = (journeys || []).filter(j => !(j.mgr && shown[j.startedMonth]))
  if (!rest.length) return null
  const done = rest.filter(j => j.finish).length
  const cats: { [c: string]: number } = {}
  rest.forEach(j => { if (j.cat) cats[j.cat] = (cats[j.cat] || 0) + 1 })
  return {
    done,
    inProgress: rest.length - done,
    topCategories: Object.entries(cats).sort((a, b) => b[1] - a[1]).slice(0, 3)
  }
}

// ---- v2: sparkline ----------------------------------------------------------
export function monthlyLevelSeries (feedback: any[], monthKeys: string[]): Array<number | null> {
  return monthKeys.map(k => {
    const lv = ratingLevelsOf(monthFeedbackRows(feedback, k))
    if (!lv.length) return null
    return lv.reduce((a, b) => a + b, 0) / lv.length
  })
}

export function sparklinePoints (series: Array<number | null>, w: number, h: number): string {
  const vals = series.map((v, i) => [i, v] as [number, number | null]).filter(([, v]) => v != null) as Array<[number, number]>
  if (vals.length < 2) return ''
  const min = Math.min(...vals.map(([, v]) => v))
  const max = Math.max(...vals.map(([, v]) => v))
  const span = (max - min) || 1
  return vals.map(([i, v]) => `${4 + (i / (series.length - 1)) * (w - 20)},${(h - 6) - ((v - min) / span) * (h - 12)}`).join(' ')
}
